const PRINT_SETTINGS_SCHEMA_VERSION = 1;
const PRINT_VIEWS_MAX_COUNT = 100;
const DEFAULT_PRINT_VIEW_PAPER_FORMAT = 'A4';
const DEFAULT_PRINT_VIEW_SCALE = 100;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function clonePrintPlanValue(value) {
  if (value === undefined) return undefined;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizedText(value) {
  return String(value ?? '').trim();
}

function defaultPrintViewIdFactory(prefix = 'print_view') {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `${prefix}_${globalThis.crypto.randomUUID()}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}_${Date.now().toString(36)}`;
}

function uniquePrintViewId(rawId, usedIds, idFactory, index) {
  const candidate = normalizedText(rawId);
  if (candidate && !usedIds.has(candidate)) return candidate;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const generated = normalizedText(idFactory('print_view', { index, attempt }));
    if (generated && !usedIds.has(generated)) return generated;
  }
  let fallback = `print_view_${index + 1}`;
  let suffix = 1;
  while (usedIds.has(fallback)) {
    suffix += 1;
    fallback = `print_view_${index + 1}_${suffix}`;
  }
  return fallback;
}

function normalizeSelectedGroupKeys(value) {
  // `null` is intentional: an untouched legacy/default view follows all
  // currently available groups. Once a checkbox is changed, callers persist
  // an explicit array (including an empty one), so later groups cannot silently
  // leak into an intentionally scoped print view.
  if (!Array.isArray(value)) return null;
  const keys = [];
  const seen = new Set();
  for (const rawKey of value) {
    const key = normalizedText(rawKey);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

function normalizePrintViewWindows(value, options = {}) {
  const source = Array.isArray(value) ? clonePrintPlanValue(value) : [];
  if (typeof options.normalizeViewWindows !== 'function') return source;
  const normalized = options.normalizeViewWindows(source);
  return Array.isArray(normalized) ? clonePrintPlanValue(normalized) : [];
}

function normalizePrintView(value, options = {}) {
  const source = isPlainObject(value) ? value : {};
  const index = Number.isSafeInteger(Number(options.index)) && Number(options.index) >= 0
    ? Number(options.index)
    : 0;
  const usedIds = options.usedIds instanceof Set ? options.usedIds : new Set();
  const idFactory = typeof options.idFactory === 'function'
    ? options.idFactory
    : defaultPrintViewIdFactory;
  const id = uniquePrintViewId(source.id, usedIds, idFactory, index);
  usedIds.add(id);

  const numericScale = Number(source.scale);
  const numericCamX = Number(source.camX);
  const numericCamZ = Number(source.camZ);
  const titleBlockId = source.titleBlockId == null || normalizedText(source.titleBlockId) === ''
    ? null
    : normalizedText(source.titleBlockId);

  return {
    id,
    sortOrder: index,
    paperFormat: source.paperFormat === 'A3' ? 'A3' : DEFAULT_PRINT_VIEW_PAPER_FORMAT,
    scale: Number.isFinite(numericScale) && numericScale > 0
      ? numericScale
      : DEFAULT_PRINT_VIEW_SCALE,
    camX: Number.isFinite(numericCamX) ? numericCamX : 0,
    camZ: Number.isFinite(numericCamZ) ? numericCamZ : 0,
    titleBlockId,
    hasPlanView: source.hasPlanView === true,
    selectedGroupKeys: normalizeSelectedGroupKeys(source.selectedGroupKeys),
    viewWindows: normalizePrintViewWindows(source.viewWindows, options)
  };
}

function printViewCandidates(value, maxViews) {
  const source = isPlainObject(value) ? value : {};
  if (Array.isArray(source.printViews)) {
    return source.printViews.filter(isPlainObject).slice(0, maxViews);
  }
  // The former, unversioned printSettings object was one flat print view.
  // Feeding it through the same view normalizer provides the migration without
  // retaining a second active-view mirror and its two-source-of-truth risk.
  return [source];
}

function normalizePrintSettings(value, options = {}) {
  const source = isPlainObject(value) ? value : {};
  const maxViews = Number.isSafeInteger(Number(options.maxViews)) && Number(options.maxViews) > 0
    ? Math.min(Number(options.maxViews), PRINT_VIEWS_MAX_COUNT)
    : PRINT_VIEWS_MAX_COUNT;
  const idFactory = typeof options.idFactory === 'function'
    ? options.idFactory
    : defaultPrintViewIdFactory;
  const usedIds = new Set();
  const candidates = printViewCandidates(source, maxViews);
  const printViews = (candidates.length ? candidates : [{}]).map((view, index) => (
    normalizePrintView(view, {
      ...options,
      idFactory,
      index,
      usedIds
    })
  ));
  const requestedActiveId = normalizedText(source.activePrintViewId);
  const activePrintViewId = printViews.some(view => view.id === requestedActiveId)
    ? requestedActiveId
    : printViews[0].id;

  return {
    schemaVersion: PRINT_SETTINGS_SCHEMA_VERSION,
    activePrintViewId,
    printViews
  };
}

function activePrintView(value, options = {}) {
  const settings = normalizePrintSettings(value, options);
  return settings.printViews.find(view => view.id === settings.activePrintViewId)
    || settings.printViews[0]
    || null;
}

function selectPrintView(value, printViewId, options = {}) {
  const settings = normalizePrintSettings(value, options);
  const targetId = normalizedText(printViewId);
  if (settings.printViews.some(view => view.id === targetId)) {
    settings.activePrintViewId = targetId;
  }
  return settings;
}

function createPrintView(value, options = {}) {
  const settings = normalizePrintSettings(value, options);
  if (settings.printViews.length >= PRINT_VIEWS_MAX_COUNT) {
    throw new Error(`Eine Zeichnungsebene kann höchstens ${PRINT_VIEWS_MAX_COUNT} Druckansichten enthalten.`);
  }
  const sourceView = options.mode === 'copy'
    ? settings.printViews.find(view => view.id === options.sourcePrintViewId)
      || settings.printViews.find(view => view.id === settings.activePrintViewId)
      || null
    : null;
  const initial = isPlainObject(options.initial) ? options.initial : {};
  const usedIds = new Set(settings.printViews.map(view => view.id));
  const view = normalizePrintView({
    ...(sourceView ? clonePrintPlanValue(sourceView) : {}),
    ...clonePrintPlanValue(initial),
    id: options.id
  }, {
    ...options,
    index: settings.printViews.length,
    usedIds
  });
  settings.printViews.push(view);
  if (options.activate !== false) settings.activePrintViewId = view.id;
  return settings;
}

function updatePrintView(value, printViewId, patch, options = {}) {
  const settings = normalizePrintSettings(value, options);
  const index = settings.printViews.findIndex(view => view.id === normalizedText(printViewId));
  if (index < 0 || !isPlainObject(patch)) return settings;
  const current = settings.printViews[index];
  const usedIds = new Set(settings.printViews.filter((_, candidateIndex) => candidateIndex !== index).map(view => view.id));
  settings.printViews[index] = normalizePrintView({
    ...current,
    ...clonePrintPlanValue(patch),
    id: current.id
  }, {
    ...options,
    index,
    usedIds
  });
  return settings;
}

function deletePrintView(value, printViewId, options = {}) {
  const settings = normalizePrintSettings(value, options);
  const targetId = normalizedText(printViewId);
  const index = settings.printViews.findIndex(view => view.id === targetId);
  if (index < 0) return settings;

  const deletingActive = settings.activePrintViewId === targetId;
  if (settings.printViews.length === 1) {
    // Keep the modal operational while still deleting the requested identity:
    // the sole view is replaced atomically by a fresh default view.
    const usedIds = new Set([targetId]);
    const replacement = normalizePrintView({}, {
      ...options,
      index: 0,
      usedIds
    });
    return {
      schemaVersion: PRINT_SETTINGS_SCHEMA_VERSION,
      activePrintViewId: replacement.id,
      printViews: [replacement]
    };
  }

  settings.printViews.splice(index, 1);
  settings.printViews.forEach((view, sortOrder) => { view.sortOrder = sortOrder; });
  if (deletingActive) {
    settings.activePrintViewId = settings.printViews[index]?.id
      || settings.printViews[index - 1]?.id
      || settings.printViews[0].id;
  }
  return settings;
}

function setPrintViewGroupSelection(value, printViewId, selectedGroupKeys, options = {}) {
  const explicitSelection = Array.isArray(selectedGroupKeys)
    ? normalizeSelectedGroupKeys(selectedGroupKeys)
    : null;
  return updatePrintView(value, printViewId, { selectedGroupKeys: explicitSelection }, options);
}

function isPrintViewGroupSelected(view, groupKey) {
  const key = normalizedText(groupKey);
  if (!key) return false;
  const selectedGroupKeys = normalizeSelectedGroupKeys(view?.selectedGroupKeys);
  return selectedGroupKeys === null || selectedGroupKeys.includes(key);
}

function selectedGroupsForPrintView(view, groups = []) {
  if (!Array.isArray(groups)) return [];
  return groups.filter(group => isPrintViewGroupSelected(
    view,
    group?.key || `${normalizedText(group?.systemId ?? group?.systemKey)}:${normalizedText(group?.groupId ?? group?.id)}`
  ));
}

function printViewTabLabel(view, options = {}) {
  const sheetTitle = normalizedText(options.sheetTitle);
  const selectedGroups = selectedGroupsForPrintView(view, options.groups);
  const groupLabel = selectedGroups
    .map(group => {
      const name = normalizedText(
        group?.displayName
        || group?.label
        || group?.name
        || group?.groupId
        || group?.id
      );
      // Sidebar metadata may contain several long technical values. The tab
      // keeps the recognizable group name plus its first compact descriptor,
      // for example "Compact 2 Türen".
      const meta = normalizedText(group?.sidebarMeta).split(/\s*·\s*/)[0];
      return [name, meta && meta !== name ? meta : ''].filter(Boolean).join(' ');
    })
    .filter(Boolean)
    .join(', ')
    || normalizedText(options.emptyGroupsLabel)
    || 'Keine Gruppen';
  return [
    sheetTitle,
    view?.paperFormat === 'A3' ? 'A3' : DEFAULT_PRINT_VIEW_PAPER_FORMAT,
    groupLabel
  ].filter(Boolean).join(' · ');
}

export {
  DEFAULT_PRINT_VIEW_PAPER_FORMAT,
  DEFAULT_PRINT_VIEW_SCALE,
  PRINT_SETTINGS_SCHEMA_VERSION,
  PRINT_VIEWS_MAX_COUNT,
  activePrintView,
  clonePrintPlanValue,
  createPrintView,
  defaultPrintViewIdFactory,
  deletePrintView,
  isPrintViewGroupSelected,
  normalizePrintSettings,
  normalizePrintView,
  normalizeSelectedGroupKeys,
  printViewTabLabel,
  selectPrintView,
  selectedGroupsForPrintView,
  setPrintViewGroupSelection,
  updatePrintView
};
